import {
    useEffect,
    useRef,
    useState
} from "react";
import { useNavigate } from "react-router-dom";
import ValidationGroup from 'devextreme-react/validation-group';
import { ROUTE_TCM_PROJECTS } from "../Routes/Routes";
import ClosingAlert from "../../components/ClosingAlert";
import { ToolbarForm } from "../../components/toolbar-form/toolbar-form";
import PutPostPatchFetch from "../../hooks/PutPostPatchFetch";
import TcmProjectFormDetails from "./TcmProjectFormDetails";
import { URL_tcmProjectAdd, URL_tcmProjectEdit } from "../TcmService";
import { removeComputedFields } from "../util/helperFunctions";
const TcmProjectForm = ({ data, isLoading = false, create = false, onReload }) => {
    const [editing, setEditing] = useState(create);
    const [formData, setFormData] = useState(data);
    const navigate = useNavigate();
    const dataRef = useRef(null);
    const validationGroupRef = useRef(null);
    const navigateToRoute = () => navigate(-1);
    const [recordSaved, setRecordSaved] = useState(false);
    const [recordSaveFail, setRecordSaveFail] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const saveEndpoint = create ? URL_tcmProjectAdd : URL_tcmProjectEdit;
    const saveMethod = create ? 'POST' : 'PUT';

    useEffect(() => {
        setFormData(data);
        if (create) {
            dataRef.current = formData;
        }
    }, [data]);


    const handleEditClick = () => {
        if (!editing && formData) {
            dataRef.current = formData;
        }
        else {
            dataRef.current = undefined;
        }
        setEditing(!editing);
    };
    const onSaveClick = async () => {
        const { isValid, brokenRules } = validationGroupRef.current.instance().validate();
        if (brokenRules) {
            console.warn(`${brokenRules.length} rules have failed due to user input.`)
        }
        if (!isValid) {
            return;
        }
        // --- Enforce BuildingId required ---
        const buildingId = formData?.BuildingId;
        if (!buildingId || buildingId <= 0) {
            setErrorMessage("A Building Number is required before saving the project.");
            setRecordSaved(false);
            setRecordSaveFail(true);
            return;
        }
        setRecordSaveFail(false);
        setRecordSaved(false);
        setErrorMessage("");
        // Deep clone to avoid mutating state
        const payload = JSON.parse(JSON.stringify(formData));

        // Strip computed fields
        removeComputedFields(payload);

        // console.log("Submitted Form data (sanitized)", payload);
        // console.log("Form data", formData)
        const { Success, Message, Errors } = await PutPostPatchFetch(saveEndpoint, saveMethod, payload);
        if (Success) {
            // method successful but may still return "false" where endpoint wasn't able to save/add record
            // console.log("Message", Message)
            if (Message) {
                setRecordSaved(true);
                setRecordSaveFail(false);
                setErrorMessage("");
            }
            if (Errors.length > 0) {
                setErrorMessage("We were unable to save this information. Please try again or contact your system's administrator");
                setRecordSaved(false);
                setRecordSaveFail(true);
            }

        }
        else {
            // 400s to 500s status codes
            console.log("What went wrong:", Errors);
            setErrorMessage("We were unable to save this information. Please try again or contact your system's administrator");
            setRecordSaved(false);
            setRecordSaveFail(true);
        }
        setEditing(false);
    };

    const onCancelClick = () => {
        setFormData(dataRef.current);
        handleEditClick();
    };
    const onBackClick = () => {
        navigate(-1);
    }
    const updateField = (field, value) => {
        if (!formData) return;

        // Split the field by dot to get all the nested levels
        const fieldParts = field.split(".");

        // Function to traverse and update the nested fields
        const updateNestedField = (obj, fieldParts, value) => {
            const currentField = fieldParts[0]; // Get the first part of the field path
            if (fieldParts.length === 1) {
                // If there's no more depth, we update the value
                obj[currentField] = value;
            } else {
                // If there are more levels, recurse into the nested object
                if (!obj[currentField]) {
                    obj[currentField] = {}; // Ensure the object exists
                }
                updateNestedField(obj[currentField], fieldParts.slice(1), value); // Recurse deeper
            }
        };

        // Start the update process
        const updatedFormData = { ...formData }; // Clone formData to avoid mutation

        // Call the recursive function to update the specific field
        updateNestedField(updatedFormData, fieldParts, value);

        // Update the state with the new formData
        setFormData(updatedFormData);
    };

    return (
        <>
                    
            <div className='tcm-project-form'>
                <ValidationGroup ref={validationGroupRef}>
                    <ToolbarForm
                        toggleEditing={handleEditClick}
                        onSaveClick={onSaveClick}
                        editing={editing}
                        onCancelClick={onCancelClick}
                        onBackClick={onBackClick}
                    />
                    <TcmProjectFormDetails
                        loading={isLoading}
                        hasData={!formData}
                        data={formData}
                        editing={editing}
                        updateField={updateField}
                        onReload={onReload}
                    />
                    {/* is there an issue by putting this in the validationGroup?
                    should this TcmCostEstimates component have update field in it?
                    or should the api be called by itself, independently saving 
                    costestimates? */}

                </ValidationGroup>
                {/* 
                below may be where we would add the 'cost estimates'
                does it need to have cost estimates on creation as well?
                {!create && <TcmCostEstimates editing={editing}/>} */}
            </div>
            <ClosingAlert
                clickableIcon={navigateToRoute}
                enableAlertIcon={true}
                message={`Record was saved successfully. Click the "Forward" icon to be routed back the ${ROUTE_TCM_PROJECTS.title} Page.`}
                severity="success"
                visible={recordSaved}
            />
            <ClosingAlert
                enableAlertIcon={true}
                clickableIcon={() => { setRecordSaveFail(false) }}
                message={errorMessage}
                severity="warning"
                visible={recordSaveFail}
            />
            {/* Below could be used if you need an edge case alert, otherwise, delete */}
            {/* <ClosingAlert
                clickableIcon={() => { setCodeMismatchAlert(false) }}
                enableAlertIcon={true}
                message={"There appears to be a mismatch between the billed and actual agency bureau codes. If this is correct, please ignore this message and continue."}
                severity="info"
                visible={codeMismatchAlert}
            /> */}
        </>
    )
}
export default TcmProjectForm;